import useAPI, { useAPIMutation, useResetAPI } from "@/services/api/hook";
import { APIObject } from "@/services/api/types";
import {
  ViewData,
  ViewDataConfiguration,
  ViewsConfigResponse,
  ViewsDataResponse,
  ViewType
} from "./types";
import { useCallback, useMemo, useRef } from "react";
import { isValidConfigResult, resolveCurrentUserFilters } from "./helpers";
import { useUsersMe } from "@/entities/users/hooks";
import { isEqual, pick } from "lodash";
import { ColumnData } from "@/entities/dataTables/types";

// High-level views and configuration operations
export const useViewsAll = (
  type: ViewType
): {
  views: ViewDataConfiguration[];
  error: string | null;
  isSuccess: boolean;
  isLoading: boolean;
  refetch: () => Promise<void>;
} => {
  const viewsResult = useAPI(
    "views/type",
    { pathParams: { typeId: type.toString() } },
    { retry: false }
  );

  const configResult = useAPI(
    "views/type/config",
    { pathParams: { typeId: type.toString() } },
    { retry: false }
  );

  const usersMeResult = useUsersMe();
  const currentUserId = usersMeResult.data?.id;

  // Process and merge data
  const views = useMemo(() => {
    if (
      viewsResult.isSuccess &&
      configResult.isSuccess &&
      usersMeResult.isSuccess &&
      viewsResult.data &&
      isValidConfigResult(configResult.data)
    ) {
      const { views } = viewsResult.data as ViewsDataResponse;
      const config =
        configResult.data && configResult.data !== ""
          ? (configResult.data as ViewsConfigResponse).config
          : { ordered: {} };

      const hasNoConfig = Object.keys(config.ordered || {}).length === 0;
      return views
        .map((view, index) => {
          const isSystem = !view.createdBy;
          return {
            ...view,
            config: {
              ...view.config,
              filters: resolveCurrentUserFilters(
                view.config.filters,
                currentUserId
              )
            },
            // If no config is defined, set all system views as open
            isOpen: hasNoConfig
              ? isSystem
              : (config.ordered?.[view.id]?.isOpen ?? false),
            isSystem,
            order: hasNoConfig
              ? index
              : Object.keys(config.ordered || {}).indexOf(view.id)
          };
        })
        .sort((a, b) => {
          if (hasNoConfig) {
            if (a.isSystem !== b.isSystem) {
              return a.isSystem ? -1 : 1;
            }
            return b.order - a.order;
          }
          return a.order - b.order;
        });
    }
    return [];
  }, [
    viewsResult.data,
    configResult.data,
    viewsResult.isSuccess,
    configResult.isSuccess,
    usersMeResult.isSuccess,
    currentUserId
  ]);

  const refetch = async () => {
    await Promise.all([
      viewsResult.refetch(),
      configResult.refetch(),
      usersMeResult.refetch()
    ]);
  };

  return {
    views,
    error: viewsResult.error || configResult.error || usersMeResult.error,
    isSuccess:
      viewsResult.isSuccess && configResult.isSuccess && usersMeResult.isSuccess,
    isLoading:
      viewsResult.isLoading || configResult.isLoading || usersMeResult.isLoading,
    refetch
  };
};

export const useUpdateViewsMutation = () => {
  const resetViewsType = useResetAPI("views/type");
  const resetViewsConfig = useResetAPI("views/type/config");

  // Mutation for updating view configuration (order, visibility)
  const configMutation = useAPIMutation<
    { pathParams: { typeId: string }; data: ViewsConfigResponse },
    void
  >("views/type/config", { method: "PUT" });

  const viewMutation = useAPIMutation<
    { pathParams: { viewId: string }; data: ViewData },
    ViewData
  >("views/id", { method: "PUT" });

  const configMutationRef = useRef(configMutation);
  configMutationRef.current = configMutation;

  const viewMutationRef = useRef(viewMutation);
  viewMutationRef.current = viewMutation;

  const resetViewsTypeRef = useRef(resetViewsType);
  resetViewsTypeRef.current = resetViewsType;

  const resetViewsConfigRef = useRef(resetViewsConfig);
  resetViewsConfigRef.current = resetViewsConfig;

  const mutate = useCallback(
    async ({
      views,
      previousViews,
      type
    }: {
      views: ViewDataConfiguration[];
      previousViews: ViewDataConfiguration[];
      type: ViewType;
    }) => {
      try {
        const config: ViewsConfigResponse = {
          config: {
            ordered: views.reduce(
              (acc, view, index) => ({
                ...acc,
                [view.id]: {
                  isOpen: view.isOpen,
                  order: index
                }
              }),
              {}
            )
          }
        };

        const updatedViews = views.filter((curr) => {
          const prev = previousViews.find((p) => p.id === curr.id);
          if (!prev) return false;

          return !isEqual(
            pick(curr, ["name", "filters", "sort", "groupBy"]),
            pick(prev, ["name", "filters", "sort", "groupBy"])
          );
        });

        await Promise.all([
          configMutationRef.current.mutateAsync({
            pathParams: { typeId: type.toString() },
            data: config
          }),

          ...updatedViews.map((view) =>
            viewMutationRef.current.mutateAsync({
              pathParams: { viewId: view.id },
              data: view
            })
          )
        ]);

        resetViewsTypeRef.current();
        resetViewsConfigRef.current();

        return {
          success: true,
          updatedViews: views
        };
      } catch (error) {
        return {
          success: false,
          error
        };
      }
    },
    []
  );

  return {
    mutate,
    isLoading: configMutation.isPending || viewMutation.isPending,
    isSuccess: configMutation.isSuccess && viewMutation.isSuccess,
    error: configMutation.error || viewMutation.error
  };
};

export const useSaveCurrentView = () => {
  const updateView = useUpdateViewMutation();

  const saveView = async (
    currentView: ViewDataConfiguration,
    updates: {
      filters: any;
      sortBy: any;
      groupBy: string[];
      searchTerm: string;
      columns?: ColumnData[];
    }
  ) => {
    return updateView.mutateAsync({
      pathParams: { viewId: currentView.id },
      data: {
        ...currentView,
        config: {
          filters: updates.filters,
          sort: updates.sortBy,
          groupBy: updates.groupBy,
          searchTerm: updates.searchTerm,
          columns: updates.columns
        }
      }
    });
  };

  return {
    saveView,
    isLoading: updateView.isPending
  };
};

// Low-level view operations
export const useCreateViewMutation = () => {
  return useAPIMutation<ViewData, APIObject>("views", {
    method: "POST"
  });
};

export const useUpdateViewMutation = () => {
  return useAPIMutation<
    { pathParams: { viewId: string }; data: ViewData },
    ViewData
  >("views/id", {
    method: "PUT"
  });
};

export const useDeleteViewMutation = () => {
  return useAPIMutation<{ pathParams: { viewId: string } }, void>("views/id", {
    method: "DELETE"
  });
};
